import { useCallback, useEffect, useState } from "react";
import { Table } from "antd";
import { isEmpty } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";

import "./ProjectList.scss";
import { ProjectFilters } from "../project-filters/ProjectFilters";
import { ProjectColumnPicker } from "../project-column-picker/ProjectColumnPicker";
import { useProjects } from "projects/hooks";
import {
  DEFAULT_VISIBLE_PROJECT_COLUMNS,
  getProjectColumns,
  getVisibleProjectColumns,
} from "projects/constants";
import { useBoundStore } from "store";
import { useLookupOptions } from "admin/queries";
import { useUpdateOrganizationUserMetadata } from "users/queries";
import { UserRoles } from "admin/constants";
import { IProject } from "projects/models";

export const ProjectList = () => {
  const { user, filters, stage, showArchived, currentOrganization, tenant } = useBoundStore();
  const isSubcontractor = user?.role === UserRoles.Subcontractor;
  const { data: utilityProgramOptions = [] } = useLookupOptions("utility_program");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [defaultProjectId, setDefaultProjectId] = useState(
    searchParams?.get("id")?.trim() ?? ""
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    cursor: undefined as string | undefined,
  });

  const status = filters?.projectStatus?.join(",") || undefined;
  const subcontractor = filters?.subcontractor?.join(",") || undefined;
  const utilityProgram = filters?.utilityProgram?.join(",") || undefined;
  const accountManager = filters?.accountManager?.join(",") || undefined;
  const auditor = filters?.auditor?.join(",") || undefined;
  const customerPaymentMethod = filters?.customerPaymentMethod?.join(",") || undefined;

  const {
    data,
    isLoading,
    isFetching,
  } = useProjects(
    {
      organizationId: currentOrganization?.id,
      projectId: defaultProjectId || undefined,
      status,
      subcontractor,
      utilityProgram,
      accountManager,
      auditor,
      customerPaymentMethod,
      stage: stage || undefined,
      showArchived,
      limit: pagination.pageSize,
      lastEvaluatedKey: pagination.cursor,
    },
    true
  );

  const projects = data?.items ?? [];

  const allColumns = getProjectColumns(isSubcontractor, utilityProgramOptions);
  const accessibleColumns = allColumns.filter((column) => !column.hidden);
  const accessibleKeys = accessibleColumns.map((column) => String(column.key));
  // Without a saved preference, show the curated default set and hide the rest.
  const defaultHiddenKeys = accessibleKeys.filter(
    (key) => !DEFAULT_VISIBLE_PROJECT_COLUMNS.includes(key)
  );
  const hiddenKeys =
    user?.metadata?.projectListHiddenColumns ?? defaultHiddenKeys;
  const tableColumns = getVisibleProjectColumns(allColumns, hiddenKeys);
  // The picker works in terms of *shown* columns (checked = visible).
  const shownKeys = accessibleKeys.filter((key) => !hiddenKeys.includes(key));

  const userId = user?.userId || user?.auth0UserId;
  const { mutate: updateMetadata } = useUpdateOrganizationUserMetadata(
    userId,
    currentOrganization?.id
  );

  // Persist the inverse (hidden keys) per the API contract. The API replaces the
  // whole metadata blob, so spread the existing keys to avoid wiping other
  // preferences (e.g. sales targets).
  const onColumnsChange = (nextShown: string[]) => {
    const nextHidden = accessibleKeys.filter((key) => !nextShown.includes(key));
    updateMetadata({
      ...(user?.metadata ?? {}),
      projectListHiddenColumns: nextHidden,
    });
  };

  const updateQuery = useCallback(
    (key: string, value: string) => {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      if (value) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const onFilterChange = useCallback(async () => {
    if (
      (!isEmpty(stage) ||
        !isEmpty(status) ||
        !isEmpty(subcontractor) ||
        !isEmpty(utilityProgram) ||
        !isEmpty(accountManager) ||
        !isEmpty(auditor) ||
        !isEmpty(customerPaymentMethod)) &&
      !isEmpty(defaultProjectId)
    ) {
      setDefaultProjectId("");
      updateQuery("id", "");
      return;
    }
  }, [
    accountManager,
    auditor,
    customerPaymentMethod,
    defaultProjectId,
    stage,
    status,
    subcontractor,
    updateQuery,
    utilityProgram,
  ]);

  useEffect(() => {
    onFilterChange();
  }, [onFilterChange]);

  // Reset pagination when filters change
  useEffect(() => {
    setPagination({
      current: 1,
      pageSize: pagination.pageSize,
      cursor: undefined,
    });
  }, [stage, status, subcontractor, utilityProgram, accountManager, auditor, customerPaymentMethod, showArchived]);

  const handleTableChange = (page: number, newPageSize: number) => {
    if (newPageSize !== pagination.pageSize) {
      // Page size changed - reset to first page
      setPagination({
        current: 1,
        pageSize: newPageSize,
        cursor: undefined,
      });
    } else if (page > pagination.current) {
      // Next page - use the cursor from current data
      setPagination({
        current: page,
        pageSize: pagination.pageSize,
        cursor: data?.lastEvaluatedKey,
      });
    } else {
      // Previous page or first page - reset cursor
      setPagination({
        current: 1,
        pageSize: pagination.pageSize,
        cursor: undefined,
      });
    }
  };

  const handleRowClick = (record: IProject) => {
    if (record.id && tenant?.id && currentOrganization?.id) {
      navigate(`/${tenant.id}/organizations/${currentOrganization.id}/projects/${record.id}`);
    }
  };

  return (
    <div data-testid='projectList'>
      <ProjectFilters />
      <div className='project-list-toolbar'>
        <ProjectColumnPicker
          columns={accessibleColumns}
          value={shownKeys}
          onChange={onColumnsChange}
        />
      </div>
      <Table
        size='middle'
        loading={isLoading || isFetching}
        rowClassName="project-list-row clickable-row"
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data?.lastEvaluatedKey
            ? (pagination.current * pagination.pageSize) + 1
            : (pagination.current - 1) * pagination.pageSize + projects.length,
          showSizeChanger: true,
          showTotal: (total, range) => {
            const actualTotal = (pagination.current - 1) * pagination.pageSize + projects.length;
            return data?.lastEvaluatedKey
              ? `${range[0]}-${range[1]} of ${actualTotal}+ projects`
              : `${range[0]}-${range[1]} of ${total} projects`;
          },
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
        }}
        columns={tableColumns}
        dataSource={projects}
        className='project-list-table'
        rowKey='id'
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};
